export declare type WidgetListener = (primeFacesWidget: any) => void;
export declare type EventType = "init" | "refresh";


export default class PrimeFacesUtil {
	private static isCreateWidgetInterceptSetup = false;
	private static createWidgetListeners: Map<string, Set<WidgetListener>>; // The key is elemId + "." + event. For example "elemId.init" or "elemId.refresh"

	static getPrimeFaces(): any {
		return (window as any).PrimeFaces;	
	}

	static getWidgetById(elemId: string): any|null {
		return PrimeFacesUtil.getPrimeFaces().getWidgetById(elemId);
	}

	/**
	 * Returns the element for the widget or null.
	 */
	static getElementForWidget(primeFacesWidget: any) {
		if (primeFacesWidget.cfg !== undefined && primeFacesWidget.cfg.id !== undefined) {
			return document.getElementById(primeFacesWidget.cfg.id);
		}
		return null;
	}

	/**
	 * Register a method to be called at the end of the create widget process of PrimeFaces.
	 */
	static addWidgetListener(eventTypes: EventType|EventType[], elemId: string, listener: WidgetListener, runImmediatelyIfAlreadyInited: boolean) {
		if (!PrimeFacesUtil.isCreateWidgetInterceptSetup) {
			PrimeFacesUtil.setupCreateWidgetIntercept();
		}
		if (PrimeFacesUtil.createWidgetListeners === undefined) {
			PrimeFacesUtil.createWidgetListeners = new Map<string, Set<WidgetListener>>();
		}

		const addListener = (eventType: EventType) => {
			const key = elemId + "." + eventType;
			let listeners = PrimeFacesUtil.createWidgetListeners.get(key);
			if (listeners === undefined) {
				listeners = new Set<WidgetListener>();
				PrimeFacesUtil.createWidgetListeners.set(key, listeners);
			}
			listeners.add(listener);
		};

		if (Array.isArray(eventTypes)) {
			(eventTypes as EventType[]).forEach(addListener);
		} else {
			addListener(eventTypes);
		}

		if (runImmediatelyIfAlreadyInited) {
			const primeFacesWidget = PrimeFacesUtil.getWidgetById(elemId);
			if (primeFacesWidget !== null && primeFacesWidget !== undefined) {
				listener.call(primeFacesWidget, primeFacesWidget);
			}
		}
	}

	static removeWidgetListener(eventTypes: EventType|EventType[], elemId: string, listener: WidgetListener): void {
		if (PrimeFacesUtil.createWidgetListeners !== undefined) {
			const removeListener = (eventType: EventType) => {
				const key = elemId + "." + eventType;
				const listeners = PrimeFacesUtil.createWidgetListeners.get(key);
				if (listeners !== undefined) {
					listeners.delete(listener);
					if (listeners.size === 0) {
						PrimeFacesUtil.createWidgetListeners.delete(key);
					}
				}
			}

			if (Array.isArray(eventTypes)) {
				(eventTypes as EventType[]).forEach(removeListener);
			} else {
				removeListener(eventTypes);
			}
		}
	}

	static notifyWidgetListeners(eventType: EventType, elemId: string, primeFacesWidget: any) {
		if (elemId === undefined || PrimeFacesUtil.createWidgetListeners === undefined) {
			return;
		}
		const key = elemId + "." + eventType;
		const listeners = PrimeFacesUtil.createWidgetListeners.get(key);
		if (listeners !== undefined) {
			listeners.forEach((listener: WidgetListener) => {
				listener.call(primeFacesWidget, primeFacesWidget);
			});
		}
	}

	private static setupCreateWidgetIntercept() {
		const pf = PrimeFacesUtil.getPrimeFaces();

		const originalCreateWidget = pf.createWidget;
		pf.createWidget = function(widgetName: string, widgetVar: string, config: any) {
			const isNew = pf.widgets[widgetVar] === undefined;
			originalCreateWidget.call(this, widgetName, widgetVar, config);
			PrimeFacesUtil.notifyWidgetListeners(
				isNew ? "init" : "refresh",
				config.id,
				pf.widgets[widgetVar]
			);
		};

		PrimeFacesUtil.isCreateWidgetInterceptSetup = true;
	}

	/** 
	 * If `widgetVar` is unknown set to `null`.
	 */
	static addDeferredRender(widgetVar: string, elemId: string, callback: () => any) {
		PrimeFacesUtil.getPrimeFaces().addDeferredRender(
			widgetVar,
			elemId,
			callback
		);
		// https://primefaces.github.io/primefaces/jsdocs/classes/primefaces.widget.deferredwidget.html#adddeferredrender
		// 		Says: addDeferredRender(widgetVar: string, container: JQuery, callback: () => boolean): void
		//		But looking at the code the second argument is the HTML id of the element.
	}

	static removeDeferredRender(widgetVar: string, elemId: string, callback: () => any) {
		const deferredRenders = PrimeFacesUtil.getPrimeFaces().deferredRenders as {widgetVar: string, elemId: string, callback: () => any}[];
		for (let i = 0; i < deferredRenders.length;) {
			const deferred = deferredRenders[i];
			if (
				deferred !== undefined
				&& deferred.widgetVar === widgetVar
				&& deferred.elemId === elemId
				&& deferred.callback === callback
			) {
				deferredRenders.splice(i, 1);
			} else {
				i++;
			}
		}
	}
}
